import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import { totalAmount, totalOf, unitRate } from '@/lib/money';
import { pairOf } from '@/lib/task-currency';
import { counterpartyName, type OfferRow } from '@/features/tasks/types';
import {
  BankPanel,
  InfoGrid,
  QuoteCard,
  QuoteHeader,
  type InfoItem,
} from '@/features/tasks/components/QuoteCard';
import { useCountdown } from '@/features/tasks/components/useCountdown';
import styles from './OfferCard.module.css';

/**
 * TEK TEKLIF KARTI — uc listede de ayni.
 *
 * ============================ BAKIS ACISI ==================================
 * `side` kartin kime gorundugunu soyler ve iki seyi degistirir:
 *   'exchange' (Tekliflerim)     -> karsi taraf MUSTERI, banka bilgisi taskData
 *   'customer' (Gelen teklifler) -> karsi taraf BURO,    banka bilgisi offerData
 * Ayni satirdan iki farkli kart cizmek yerine tek kart, cunku duzenin ayni
 * kalmasi kullanicinin bilgiyi ayni yerde aramasini saglar.
 *
 * ============================== TOPLAM ======================================
 * `offerPrice` BIRIM KUR. Toplam yalniz `totalOf()` ile uretilir; ikisi ayri
 * markali tip oldugu icin biri otekinin yerine YAZILAMAZ (derleme hatasi).
 */
export type OfferCardProps = {
  offer: OfferRow;
  receivedAtMs: number;
  side: 'customer' | 'exchange';
  /** Gorev karara baglandi mi — SATIRA degil GOREVE ait bilgi. */
  taskDecided?: boolean;
  /**
   * Talebi ACAN kisinin adi. YALNIZ musteri organizasyonu yoneticisine, BASKASININ talebine
   * gelen tekliflerde veriliyor: yonetici org'un tum tekliflerini gorur ve hangi talebe ait
   * olduklari yazmazsa liste okunamaz hale gelir. Kendi talebinde her satira kendi adini
   * yazmak gurultu olurdu, o yuzden cagiran tarafta kosullu.
   */
  requestOwnerName?: string | undefined;
  onSelect?: (() => void) | undefined;
  /**
   * Kartin altina eklenen aksiyon alani (or. buronun "Odeme alindi" onayi).
   * Kartin kendisi aksiyon BILMIYOR: hangi aksiyonun gecerli oldugu listeye ve cagirana bagli,
   * uc liste de ayni karti kullaniyor.
   */
  footer?: ReactNode;
};

export function OfferCard({
  offer,
  receivedAtMs,
  side,
  taskDecided = false,
  requestOwnerName,
  onSelect,
  footer,
}: OfferCardProps) {
  const { t } = useTranslation();
  // Secilmeyen buro (M1): talebin fazi (musterinin odeme sayaci) bu buroyu ILGILENDIRMIYOR.
  // Kart kapanmis gibi cizilir ve rozet ne oldugunu soyler: "Baska buroya verildi".
  const lostForOffice = side === 'exchange' && taskDecided && !offer.isAccepted;
  const phase = useCountdown({
    taskStatusCode: offer.taskStatusCode,
    countdownStatus: lostForOffice ? 'passive' : offer.countdownStatus,
    // Teklif satirinda sunucu `statusUpdatedAt` GONDERMIYOR; geri sayim
    // `countdown` + alinma ani uzerinden mutlaklasiyor.
    statusUpdatedAt: offer.statusUpdatedAt,
    countdown: offer.countdown,
    receivedAtMs,
  });

  const pair = pairOf(offer.taskTypeCode);
  const rate = unitRate(offer.offerPrice);
  const won = taskDecided && offer.isAccepted;
  const lost = taskDecided && !offer.isAccepted;

  const businessName =
    side === 'exchange' ? offer.taskUserBusinessName : offer.offerUserBusinessName;
  const fullName = side === 'exchange' ? offer.taskUserFullName : offer.offerUserFullName;

  const info: InfoItem[] = [
    {
      label: t('labels.price'),
      rate,
      currency: pair.secondCurrency,
      emphasis: side === 'customer',
    },
    {
      label: t('labels.total'),
      total: totalOf(rate, offer.taskAmount),
      currency: pair.secondCurrency,
      emphasis: side === 'exchange',
    },
    {
      label: businessName ? t('labels.company') : t('labels.name'),
      text: counterpartyName(businessName, fullName) ?? '',
    },
  ];

  // Talep sahibi satiri en SONA: karsi taraf (teklifi veren buro) bu ekranin asil konusu,
  // sahiplik ise yalnizca yoneticinin ihtiyac duydugu bir ayirt edici.
  if (requestOwnerName !== undefined) {
    info.push({ label: t('task.owner'), text: requestOwnerName });
  }

  const card = (
    <QuoteCard phase={phase} won={won} lost={lost}>
      {won ? (
        <span className={styles.wonBadge}>
          <Trophy size={13} strokeWidth={2.2} aria-hidden="true" />
          {/* Buro icin "Kazandiniz": "Onaylanan" musteri sekmesinin de adi ve buroya ne
              oldugunu soylemiyordu (M1). Musteri tarafi M6'da ele alinacak. */}
          {side === 'exchange' ? t('task.badges.won') : t('task.tabs.approved')}
        </span>
      ) : null}

      <QuoteHeader
        // Teklif satirinda gorunur kimlik GOREVIN kimligidir: kullanici gorevi
        // izliyor, teklif kaydinin kendi kimligini hicbir yerde gormuyor.
        id={offer.taskId}
        taskTypeCode={offer.taskTypeCode}
        isBuying={offer.taskIsBuying}
        amount={totalAmount(offer.taskAmount)}
        amountCurrency={pair.currency}
        createdAt={offer.createdAt}
        phase={phase}
        warning={offer.taskStatusCode === 'warning'}
        statusCode={lostForOffice ? undefined : offer.taskStatusCode}
        badgeLabel={lostForOffice ? t('task.badges.lost') : undefined}
      />

      <InfoGrid items={info} />

      <BankPanel
        account={
          side === 'exchange'
            ? offer.taskData?.selectedAccount
            : offer.offerData?.selectedAccount
        }
      />

      {footer}
    </QuoteCard>
  );

  // Footer bir aksiyon iceriyorsa kart TIKLANABILIR SARMALANMAZ: buton icinde buton gecersiz
  // HTML ve tiklama hedefi belirsizlesirdi.
  if (!onSelect || footer) return card;

  return (
    <button type="button" className={styles.selectable} onClick={onSelect}>
      {card}
    </button>
  );
}
